/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

// A workflow drawn as a graph, and the BPMN the repository's own engine runs
// for it.
//
// The repository deploys a process when a bpm:workflowDefinition holding its
// BPMN is switched on in "Data Dictionary/Workflow Definitions" (measured on
// the deployed server). So a drawn flow becomes a real process definition: the
// engine routes approvals, branches and parallel paths itself, and nothing
// depends on a browser staying open between steps.
//
// The BPMN uses only what the repository ships: wf:activitiReviewTask for an
// approval (its outcome copied to the process by the same listener the stock
// review process uses), wf:adhocTask for a task, and gateways. Every name and
// value written into it is escaped, and a condition's field must be a plain
// property name, so nothing drawn can become an expression of its own.

export type FlowNodeKind = 'start' | 'end' | 'approval' | 'task' | 'condition' | 'parallel';

/** Who does an approval or a task: a person, anyone in a group (a pooled task), or whoever started the flow. */
export type FlowAssigneeType = 'person' | 'group' | 'initiator';

export interface FlowNode {
  id: string;
  kind: FlowNodeKind;
  name: string;
  /** Top-left corner on the canvas, in canvas pixels. */
  x: number;
  y: number;
  assigneeType?: FlowAssigneeType;
  /** A user id, or a group id (with or without GROUP_). */
  assignee?: string;
  assigneeLabel?: string;
}

export type FlowConditionOp = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'empty' | 'notEmpty';

export interface FlowCondition {
  /** A property of the flow's first document, e.g. dms:amount — read when the flow reaches the condition. */
  field: string;
  op: FlowConditionOp;
  value?: string;
}

export interface FlowEdge {
  id: string;
  from: string;
  to: string;
  /** Leaving an approval: which decision takes this path. Unset = both decisions. */
  outcome?: 'approve' | 'reject';
  /** Leaving a condition: when this path is taken. */
  condition?: FlowCondition;
  /** Leaving a condition: the path taken when no other one matches. */
  isDefault?: boolean;
}

export interface FlowGraph {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

/** What stops a graph from being published, by node or edge. */
export interface FlowProblem {
  code: string;
  nodeId?: string;
  edgeId?: string;
}

export const NODE_WIDTH = 200;
export const NODE_HEIGHT = 64;

const FIELD = /^[A-Za-z][A-Za-z0-9_]*(:[A-Za-z][A-Za-z0-9_]*)?$/;
const USER_ID = /^[^\s<>&"'${}]{1,100}$/;

/** The process key a template deploys under — valid as an XML id, and recognisable as ours. */
export function processKey(templateId: string): string {
  return 'acaFlow_' + templateId.replace(/[^A-Za-z0-9_]/g, '_').slice(0, 80);
}

/** Whether a deployed definition is one this builder made. */
export function isFlowKey(key: string | undefined): boolean {
  return !!key && key.startsWith('acaFlow_');
}

/** The process variable a document property travels in: dms:amount → dms_amount. */
export function variableName(field: string): string {
  return field.replace(':', '_');
}

export function outgoing(graph: FlowGraph, nodeId: string): FlowEdge[] {
  return graph.edges.filter((e) => e.from === nodeId);
}

export function incoming(graph: FlowGraph, nodeId: string): FlowEdge[] {
  return graph.edges.filter((e) => e.to === nodeId);
}

/** Everything that would make the deployed process wrong or impossible, in drawing order. */
export function validateGraph(graph: FlowGraph): FlowProblem[] {
  const problems: FlowProblem[] = [];
  const starts = graph.nodes.filter((n) => n.kind === 'start');
  const ends = graph.nodes.filter((n) => n.kind === 'end');
  if (starts.length !== 1) {
    problems.push({ code: starts.length ? 'ONE_START' : 'NO_START' });
  }
  if (!ends.length) {
    problems.push({ code: 'NO_END' });
  }
  if (!graph.nodes.some((n) => n.kind === 'approval' || n.kind === 'task')) {
    problems.push({ code: 'NO_STEP' });
  }

  for (const edge of graph.edges) {
    if (!graph.nodes.some((n) => n.id === edge.from) || !graph.nodes.some((n) => n.id === edge.to)) {
      problems.push({ code: 'DANGLING', edgeId: edge.id });
    }
  }

  for (const node of graph.nodes) {
    const outs = outgoing(graph, node.id);
    const ins = incoming(graph, node.id);

    if (node.kind !== 'end' && !outs.length) {
      problems.push({ code: 'NO_WAY_ON', nodeId: node.id });
    }
    if (node.kind === 'end' && outs.length) {
      problems.push({ code: 'END_LEADS_ON', nodeId: node.id });
    }
    if (node.kind === 'start' && ins.length) {
      problems.push({ code: 'START_ENTERED', nodeId: node.id });
    }

    if (node.kind === 'approval' || node.kind === 'task') {
      if (!node.name.trim()) {
        problems.push({ code: 'NO_NAME', nodeId: node.id });
      }
      const type = node.assigneeType ?? 'person';
      if (type !== 'initiator' && !(node.assignee ?? '').trim()) {
        problems.push({ code: 'NO_ASSIGNEE', nodeId: node.id });
      } else if (type !== 'initiator' && !USER_ID.test(node.assignee ?? '')) {
        problems.push({ code: 'BAD_ASSIGNEE', nodeId: node.id });
      }
    }

    if (node.kind === 'approval' && outs.length) {
      const approve = outs.filter((e) => e.outcome !== 'reject');
      const reject = outs.filter((e) => e.outcome !== 'approve');
      if (approve.length !== 1 || reject.length !== 1) {
        problems.push({ code: 'APPROVAL_PATHS', nodeId: node.id });
      }
    }
    if (node.kind === 'task' && outs.length > 1) {
      problems.push({ code: 'TASK_ONE_PATH', nodeId: node.id });
    }
    if (node.kind === 'condition' && outs.length) {
      const defaults = outs.filter((e) => e.isDefault);
      if (defaults.length !== 1) {
        problems.push({ code: 'CONDITION_DEFAULT', nodeId: node.id });
      }
      for (const edge of outs.filter((e) => !e.isDefault)) {
        if (!edge.condition || !FIELD.test(edge.condition.field ?? '')) {
          problems.push({ code: 'CONDITION_FIELD', edgeId: edge.id });
        }
      }
    }
    if (node.kind === 'parallel' && outs.length > 1 && ins.length > 1) {
      problems.push({ code: 'PARALLEL_BOTH', nodeId: node.id });
    }
    if (node.kind === 'parallel' && outs.length <= 1 && ins.length <= 1) {
      problems.push({ code: 'PARALLEL_SINGLE', nodeId: node.id });
    }
  }

  // every node must be reachable from the start, and lead to an end
  const start = starts[0];
  if (start) {
    const seen = reach(start.id, (id) => outgoing(graph, id).map((e) => e.to));
    for (const node of graph.nodes.filter((n) => !seen.has(n.id))) {
      problems.push({ code: 'UNREACHABLE', nodeId: node.id });
    }
  }
  const toEnd = new Set<string>();
  for (const end of ends) {
    for (const id of reach(end.id, (n) => incoming(graph, n).map((e) => e.from))) {
      toEnd.add(id);
    }
  }
  for (const node of graph.nodes.filter((n) => ends.length && !toEnd.has(n.id))) {
    problems.push({ code: 'NO_END_REACHED', nodeId: node.id });
  }
  return problems;
}

function reach(from: string, next: (id: string) => string[]): Set<string> {
  const seen = new Set<string>([from]);
  const queue = [from];
  while (queue.length) {
    for (const id of next(queue.shift() as string)) {
      if (!seen.has(id)) {
        seen.add(id);
        queue.push(id);
      }
    }
  }
  return seen;
}

// ── BPMN ──────────────────────────────────────────────────────────────────────

const xml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');

/** A JUEL string literal: single-quoted, its quotes and backslashes escaped. */
const literal = (text: string): string => `'${text.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;

/** An id the BPMN can use for a node or edge, whatever the editor called it. */
const ref = (prefix: string, id: string): string => `${prefix}_${id.replace(/[^A-Za-z0-9_]/g, '_')}`;

/** The JUEL condition for one branch of a condition node. Variables are read with getVariable so a missing one is null, not an error. */
export function conditionExpression(condition: FlowCondition): string {
  const variable = `execution.getVariable(${literal(variableName(condition.field))})`;
  const raw = condition.value ?? '';
  const numeric = raw.trim() !== '' && !isNaN(Number(raw));
  const value = numeric ? String(Number(raw)) : literal(raw);
  switch (condition.op) {
    case 'eq':
      return `\${${variable} == ${value}}`;
    case 'ne':
      return `\${${variable} != ${value}}`;
    case 'gt':
      return `\${${variable} != null && ${variable} > ${value}}`;
    case 'gte':
      return `\${${variable} != null && ${variable} >= ${value}}`;
    case 'lt':
      return `\${${variable} != null && ${variable} < ${value}}`;
    case 'lte':
      return `\${${variable} != null && ${variable} <= ${value}}`;
    case 'contains':
      return `\${${variable} != null && ${variable}.toString().contains(${literal(raw)})}`;
    case 'empty':
      return `\${empty ${variable}}`;
    case 'notEmpty':
      return `\${not empty ${variable}}`;
  }
}

/** The repository's own listener class for task scripts, as the stock processes use it. */
const SCRIPT_LISTENER = 'org.alfresco.repo.workflow.activiti.tasklistener.ScriptTaskListener';

/** Its counterpart for a point in the flow rather than a task. */
const EXECUTION_LISTENER = 'org.alfresco.repo.workflow.activiti.listener.ScriptExecutionListener';

/**
 * Copies the fields a condition reads from the flow's first document onto the
 * process, as the flow reaches the condition. The start cannot carry them: the
 * repository drops any start variable its start form does not define (measured
 * — dms_amount sent at the start never arrived). Reading them here also means
 * the condition sees the document as it is now, not as it was at the start.
 * Field names are checked against {@link FIELD} before they get here.
 */
function readFieldsScript(fields: string[]): string {
  const lines = ['var items = bpm_package.children;', 'var doc = items.length > 0 ? items[0] : null;'];
  for (const field of fields) {
    lines.push(`execution.setVariable('${variableName(field)}', doc != null ? doc.properties['${field}'] : null);`);
  }
  return lines.join('\n');
}

function listener(event: 'create' | 'complete', script: string): string {
  return (
    `<activiti:taskListener event="${event}" class="${SCRIPT_LISTENER}">` +
    `<activiti:field name="script"><activiti:string>${xml(script)}</activiti:string></activiti:field>` +
    `</activiti:taskListener>`
  );
}

/** Due date and priority from the start, onto each task — as the stock processes do it. */
const CREATE_SCRIPT =
  "if (typeof bpm_workflowDueDate != 'undefined') task.dueDate = bpm_workflowDueDate;\n" +
  "if (typeof bpm_workflowPriority != 'undefined') task.priority = bpm_workflowPriority;";

/** An approval's decision, from the task onto the process, where the gateway after it reads it. */
const OUTCOME_SCRIPT = "execution.setVariable('wf_reviewOutcome', task.getVariable('wf_reviewOutcome'));";

function assignment(node: FlowNode): string {
  const type = node.assigneeType ?? 'person';
  if (type === 'initiator') {
    return '<humanPerformer><resourceAssignmentExpression><formalExpression>${initiator.properties.userName}</formalExpression></resourceAssignmentExpression></humanPerformer>';
  }
  const who = (node.assignee ?? '').trim();
  if (type === 'group') {
    // an unqualified potential owner is a group to the engine: anyone in it can claim the task
    const group = who.startsWith('GROUP_') ? who : `GROUP_${who}`;
    return `<potentialOwner><resourceAssignmentExpression><formalExpression>${xml(group)}</formalExpression></resourceAssignmentExpression></potentialOwner>`;
  }
  return `<humanPerformer><resourceAssignmentExpression><formalExpression>${xml(who)}</formalExpression></resourceAssignmentExpression></humanPerformer>`;
}

/**
 * The BPMN 2.0 document for a valid graph. Call {@link validateGraph} first:
 * this writes what it is given.
 */
export function toBpmn(graph: FlowGraph, key: string, name: string): string {
  const parts: string[] = [];
  const nodeRef = (id: string): string => {
    const node = graph.nodes.find((n) => n.id === id);
    return node?.kind === 'approval' && outgoing(graph, id).some((e) => e.outcome) ? ref('gw', id) : ref('n', id);
  };

  for (const node of graph.nodes) {
    const id = ref('n', node.id);
    const label = xml(node.name || node.kind);
    switch (node.kind) {
      case 'start':
        parts.push(`<startEvent id="${id}" name="${label}" activiti:formKey="bpm:startTask"/>`);
        break;
      case 'end':
        parts.push(`<endEvent id="${id}" name="${label}"/>`);
        break;
      case 'approval':
      case 'task': {
        const formKey = node.kind === 'approval' ? 'wf:activitiReviewTask' : 'wf:adhocTask';
        const listeners = listener('create', CREATE_SCRIPT) + (node.kind === 'approval' ? listener('complete', OUTCOME_SCRIPT) : '');
        parts.push(
          `<userTask id="${id}" name="${label}" activiti:formKey="${formKey}">` +
            `<extensionElements>${listeners}</extensionElements>${assignment(node)}</userTask>`
        );
        // an approval whose decisions part ways is followed by the gateway that reads the decision
        if (node.kind === 'approval' && outgoing(graph, node.id).some((e) => e.outcome)) {
          parts.push(`<exclusiveGateway id="${ref('gw', node.id)}" name="${label}"/>`);
          parts.push(`<sequenceFlow id="${ref('to', node.id)}" sourceRef="${id}" targetRef="${ref('gw', node.id)}"/>`);
        }
        break;
      }
      case 'condition': {
        const outs = outgoing(graph, node.id);
        const fallback = outs.find((e) => e.isDefault);
        const fields = [
          ...new Set(
            outs
              .filter((e) => !e.isDefault)
              .map((e) => e.condition?.field ?? '')
              .filter((f) => FIELD.test(f))
          )
        ];
        const read = fields.length
          ? `<extensionElements><activiti:executionListener event="start" class="${EXECUTION_LISTENER}">` +
            `<activiti:field name="script"><activiti:string>${xml(readFieldsScript(fields))}</activiti:string></activiti:field>` +
            `</activiti:executionListener></extensionElements>`
          : '';
        parts.push(`<exclusiveGateway id="${id}" name="${label}"${fallback ? ` default="${ref('f', fallback.id)}"` : ''}>${read}</exclusiveGateway>`);
        break;
      }
      case 'parallel':
        parts.push(`<parallelGateway id="${id}" name="${label}"/>`);
        break;
    }
  }

  for (const edge of graph.edges) {
    const source = graph.nodes.find((n) => n.id === edge.from);
    const sourceRef = nodeRef(edge.from);
    let condition = '';
    if (source?.kind === 'approval' && edge.outcome) {
      condition = edge.outcome === 'approve' ? "${wf_reviewOutcome == 'Approve'}" : "${wf_reviewOutcome == 'Reject'}";
    } else if (source?.kind === 'condition' && !edge.isDefault && edge.condition) {
      condition = conditionExpression(edge.condition);
    }
    // escaped rather than CDATA: a value could itself contain "]]>"
    const body = condition ? `<conditionExpression xsi:type="tFormalExpression">${xml(condition)}</conditionExpression>` : '';
    parts.push(
      `<sequenceFlow id="${ref('f', edge.id)}" sourceRef="${sourceRef}" targetRef="${ref('n', edge.to)}"${body ? `>${body}</sequenceFlow>` : '/>'}`
    );
  }

  return (
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    '<definitions xmlns="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:activiti="http://activiti.org/bpmn"' +
    ' xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" targetNamespace="http://www.alfresco.org/aca/flows">\n' +
    `<process id="${xml(key)}" name="${xml(name)}" isExecutable="true">\n` +
    parts.join('\n') +
    '\n</process>\n</definitions>\n'
  );
}

// ── from the step list ────────────────────────────────────────────────────────

/** The step list a template held before graphs — the shape {@link fromSteps} reads. */
export interface LegacyStep {
  id: string;
  name: string;
  type: 'review' | 'task';
  assigneeType: 'person' | 'group';
  assignee: string;
  assigneeLabel?: string;
  condition?: FlowCondition;
  onRejectStep?: number;
}

/**
 * Draws a template's step list as a graph, top to bottom, with the meaning it
 * had: a step with a condition is skipped when the condition is false, an
 * approval's rejection goes back to the step it named or ends the flow, and
 * approving goes on to the next step.
 */
export function fromSteps(steps: LegacyStep[]): FlowGraph {
  const x = 120;
  const gap = 110;
  let y = 30;
  const nodes: FlowNode[] = [];
  const edges: FlowEdge[] = [];
  let edgeCount = 0;
  const edge = (from: string, to: string, extra: Partial<FlowEdge> = {}) => edges.push({ id: `e${++edgeCount}`, from, to, ...extra });

  const start: FlowNode = { id: 'start', kind: 'start', name: 'เริ่ม', x, y };
  nodes.push(start);
  y += gap;

  // each step's own node, and the gate in front of it when it has a condition
  const entry: string[] = [];
  const stepNodes: FlowNode[] = steps.map((s) => {
    let gate: FlowNode | null = null;
    if (s.condition?.field) {
      gate = { id: `if_${s.id}`, kind: 'condition', name: `${s.condition.field}?`, x, y };
      nodes.push(gate);
      y += gap;
    }
    const node: FlowNode = {
      id: s.id,
      kind: s.type === 'review' ? 'approval' : 'task',
      name: s.name,
      x,
      y,
      assigneeType: s.assigneeType,
      assignee: s.assignee,
      assigneeLabel: s.assigneeLabel
    };
    nodes.push(node);
    entry.push(gate?.id ?? node.id);
    y += gap;
    return node;
  });
  const end: FlowNode = { id: 'end', kind: 'end', name: 'สิ้นสุด', x, y };
  nodes.push(end);

  edge(start.id, entry[0] ?? end.id);
  steps.forEach((s, i) => {
    const next = entry[i + 1] ?? end.id;
    if (s.condition?.field) {
      edge(`if_${s.id}`, stepNodes[i].id, { condition: { ...s.condition } });
      edge(`if_${s.id}`, next, { isDefault: true });
    }
    if (s.type === 'review') {
      edge(stepNodes[i].id, next, { outcome: 'approve' });
      const back = s.onRejectStep !== undefined ? entry[s.onRejectStep] : undefined;
      edge(stepNodes[i].id, back ?? end.id, { outcome: 'reject' });
    } else {
      edge(stepNodes[i].id, next);
    }
  });
  return { nodes, edges };
}

/** The approvals and tasks in the order a flow reaches them — for summaries and the wizard's preview. */
export function flowSteps(graph: FlowGraph): FlowNode[] {
  const start = graph.nodes.find((n) => n.kind === 'start');
  if (!start) {
    return [];
  }
  const order: FlowNode[] = [];
  for (const id of reach(start.id, (n) => outgoing(graph, n).map((e) => e.to))) {
    const node = graph.nodes.find((n) => n.id === id);
    if (node && (node.kind === 'approval' || node.kind === 'task')) {
      order.push(node);
    }
  }
  return order;
}
